import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useForm } from "@tanstack/react-form";
import { updatePassword } from "firebase/auth";
import { Button } from "@sellar-io/components";
import { useDeliveryPreferencesStore } from "../deliveryPreferencesStore";
import { accountSetupSchema } from "../stepRegistry";
import { OrderBanner } from "../components/OrderBanner";
import { OrderSummaryDrawer } from "../components/OrderSummaryDrawer";
import { useOrderLinkFlowOrder } from "../../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";
import { useOrderLinkFlowAccountFinalise } from "../../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowAccountFinalise";
import { useOrderLinkFlowDeliveryPreferences } from "../../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowDeliveryPreferences";
import { useFirebaseUser } from "../../../../../hooks/useFirebaseUser.shared";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";

const inputClass =
  "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-800 outline-none focus:border-zinc-400";

export default function AccountSetupStep() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: order } = useOrderLinkFlowOrder(token);
  const store = useDeliveryPreferencesStore();
  const { auth } = useFirebaseUser();
  const accountFinaliseMutation = useOrderLinkFlowAccountFinalise(token);
  const deliveryPreferencesMutation =
    useOrderLinkFlowDeliveryPreferences(token);

  const form = useForm({
    defaultValues: {
      password: store.password,
      confirmPassword: store.confirmPassword,
      acceptedTerms: store.acceptedTerms,
      supplierAcknowledgement: store.supplierAcknowledgement,
    },
    validators: {
      onSubmit: accountSetupSchema,
    },
    onSubmit: async ({ value }) => {
      if (!token || !auth.currentUser) return;

      setSubmitError(null);
      setIsSubmitting(true);
      store.setAccountSetup(value);

      try {
        await updatePassword(auth.currentUser, value.password);

        const firebaseIdToken = await auth.currentUser.getIdToken();

        await accountFinaliseMutation.mutateAsync({
          firebaseIdToken,
          name: store.buyerDetails?.name ?? "",
          smsNumber: store.buyerDetails?.phone ?? "",
        });

        if (store.deliveryTime && store.deliveryContact && store.buyerDetails) {
          const { additionalContacts, ...deliveryContact } =
            store.deliveryContact;

          await deliveryPreferencesMutation.mutateAsync({
            deliveryTime: store.deliveryTime,
            deliveryContact,
            additionalContacts,
            buyerDetails: store.buyerDetails,
          });
        }

        store.next();
      } catch (error) {
        const apiError = apiV2AssertError(error);
        setSubmitError(
          apiError?.response?.data.message ??
            "Something went wrong. Please try again.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const buyerName = store.buyerDetails?.name ?? "";

  return (
    <div className="flex min-h-screen flex-col">
      {order && (
        <OrderBanner order={order} onShowDetails={() => setDrawerOpen(true)} />
      )}

      <div className="w-full flex-1 border-t border-zinc-200 bg-neutral-100 px-5 py-8">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-5">
            <h2 className="text-xl font-semibold text-zinc-800">
              One last thing - please set a password
            </h2>
            <p className="mt-1 text-sm text-zinc-500">
              Set a secure password to create your account
            </p>
          </div>

          {buyerName && (
            <div className="mb-4">
              <p className="mb-2 text-sm font-medium text-zinc-800">
                Your account
              </p>
              <div className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3">
                {/* Account icon svg */}
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-200">
                  <svg
                    className="h-5 w-5 text-zinc-500"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12 12a5 5 0 100-10 5 5 0 000 10zm-7 8a7 7 0 0114 0H5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-zinc-800">
                    {buyerName}
                  </p>
                  {order?.retailerName && (
                    <p className="text-xs text-zinc-500">
                      {order.retailerName}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-4"
          >
            <form.Field name="password">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-zinc-800">
                    Set password
                  </label>
                  <input
                    type="password"
                    className={inputClass}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="confirmPassword">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-zinc-800">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    className={inputClass}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="acceptedTerms">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={field.state.value}
                      onChange={(e) => field.handleChange(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 accent-zinc-800"
                    />
                    <span className="text-sm text-zinc-700">
                      I agree to Sellar's
                      <Link
                        to={"https://www.sellar.io/terms"}
                        target={"_blank"}
                        className={"pl-1 text-center text-blue-600 underline"}
                      >
                        Terms and Conditions
                      </Link>
                    </span>
                  </label>
                  {field.state.meta.errors.length > 0 && (
                    <p className="ml-7 text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="supplierAcknowledgement">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={field.state.value}
                      onChange={(e) => field.handleChange(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 accent-zinc-800"
                    />
                    <span className="text-sm text-zinc-700">
                      I confirm I am associated with, and have permission to
                      order on behalf of this business
                    </span>
                  </label>
                  {field.state.meta.errors.length > 0 && (
                    <p className="ml-7 text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {submitError && (
              <p className="text-sm text-red-500">{submitError}</p>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="mt-1 w-full cursor-pointer rounded-xl bg-zinc-900 py-3.5 text-sm font-semibold tracking-wide text-white uppercase transition-colors hover:bg-zinc-800 disabled:opacity-60"
            >
              {isSubmitting ? "Creating account\u2026" : "Set password"}
            </Button>
          </form>
        </div>
      </div>

      {order && (
        <OrderSummaryDrawer
          active={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          order={order}
        />
      )}
    </div>
  );
}
